// ADR-0084 amendment (2026-08-08): the render-time regression gate checks
// BOTH sides of the comparison, like the scoring app does.
//
// Why: blobs scored before the ADR-0084 scoring deploy (2026-08-05) carry
// regressed_from_passed=true baked into the immutable snapshot even when
// their baseline was a Patriot-editable Excel import. #451's current-side
// gate hid those badges only accidentally (the envelope key was absent);
// #454's side-car injection then supplied collection_method for the CURRENT
// report and re-exposed the stale flags (arnold-and-porter, 39 rows). The
// historical timestamp join was superseded by the exact publication identity
// contract. The stored predecessor and authorized response comparison must
// agree; the eligible list supplies the matching predecessor's provenance.
//
// Fail-closed contract (same as regression_policy.py): no exact predecessor,
// no matching eligible identity, or a matched scan without collection_method ===
// "securescan365" all return false. KNOWN INTERACTION: archiving the
// predecessor removes it from the scan list, so a legitimate SS365 -> SS365
// pair loses its badges once the prior scan is archived — same family as
// the flag-5 regression-vs-archived-predecessor task.
const completeIdentity = identity => typeof identity?.scored_path === "string"
  && identity.scored_path.length > 0 && typeof identity?.sha256 === "string"
  && /^[a-f0-9]{64}$/.test(identity.sha256);

export const exactScoringBaseline = (report, scans) => {
  const comparison = report?._publication?.comparison;
  const predecessor = comparison?.scoring_predecessor;
  const stored = report?.scoring_predecessor;
  if (!completeIdentity(predecessor) || !completeIdentity(stored) || predecessor.scored_path !== stored.scored_path
      || predecessor.sha256 !== stored.sha256 || !Array.isArray(scans)) return null;
  if (report.collection_method !== "securescan365" || comparison.collection_method !== "securescan365") return null;
  return scans.find(scan => completeIdentity(scan?.identity) && scan.identity.scored_path === predecessor.scored_path
    && scan.identity?.sha256 === predecessor.sha256
    && scan.collection_method === "securescan365") || null;
};

export const publicationScansMatch = (report, payload) => {
  const publication = report?._publication;
  if (!publication || publication.kind === "legacy_pointer") return true;
  return typeof publication.selector === "string" && publication.selector.length > 0
    && completeIdentity(publication.identity)
    && Number.isInteger(publication.state_generation) && publication.state_generation >= 0
    && publication.state_generation === payload?.state_generation
    && Array.isArray(payload?.scans) && payload.scans.some(scan => (
      scan?.scan_id === publication.selector && scan.identity?.sha256 === publication.identity.sha256
      && scan.identity?.scored_path === publication.identity.scored_path
    ));
};

export const baselineIsSecureScan365 = (report, scans) => {
  // Publication-state reports carry the exact predecessor selected while
  // scoring.  This is intentionally not a timestamp join: an archived or
  // revoked predecessor is no longer eligible and therefore fails closed.
  const publicationComparison = report?._publication?.comparison;
  if (publicationComparison) {
    const predecessor = publicationComparison.scoring_predecessor;
    if (!predecessor || typeof predecessor.scored_path !== "string"
        || !/^processed\/.+/.test(predecessor.scored_path)
        || typeof predecessor.sha256 !== "string" || !/^[a-f0-9]{64}$/.test(predecessor.sha256)) return false;
    return !!exactScoringBaseline(report, scans);
  }
  // A state-bearing response with no exact eligible predecessor is an
  // explicit fail-closed result. Never fall back to a neighboring timestamp.
  if (report?._publication) return false;
  return false;
};
